import type { TooltipData } from '@frontend/charts';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';

type ChartGroup = { name: string; values: Record<string, number> };

interface CreateBarCountTooltipDataParams {
  groups: ChartGroup[];
  stackKeys: string[];
  /** Darker shade per stack key used for the count text so it stays legible. */
  labelColors: Record<string, string>;
  labels: Record<string, string>;
}

/**
 * Builds a `customTooltipData` callback for a stacked bar chart so each count
 * value renders in a darker `labelColors` shade instead of the lighter bar fill
 * color used by the default tooltip. The returned function is keyed by the
 * group's display name (the value passed to `customTooltipData`).
 */
export const createBarCountTooltipData = ({
  groups,
  stackKeys,
  labelColors,
  labels,
}: CreateBarCountTooltipDataParams) => {
  const valuesByName = groups.reduce<Record<string, Record<string, number>>>((acc, group) => {
    acc[group.name] = group.values;
    return acc;
  }, {});

  return (groupName: string): TooltipData[] => {
    const values = valuesByName[groupName] ?? {};
    return stackKeys.map((id) => ({
      color: labelColors[id] ?? theme.colors.neutral70,
      formattedValue: formatters.value(values[id] ?? 0),
      groupId: groupName,
      id,
      label: labels[id] ?? id,
      value: values[id] ?? 0,
    }));
  };
};

type SummaryEntry = { name?: string; total?: number };

/**
 * Reduces the API `summary` array into a `{ [name]: count }` map suitable for the
 * `values` prop on `Chart.Legends`, which renders the count beside each legend label.
 */
export const getLegendCountsFromSummary = (summary?: SummaryEntry[]): Record<string, number> =>
  (summary ?? []).reduce<Record<string, number>>((acc, { name, total }) => {
    if (name) {
      acc[name] = Number(total ?? 0);
    }
    return acc;
  }, {});
